// The order's tracking numbers as links in What's next (2026-10-29,
// docs/decisions/2026-10-29-items-widget-fast-rows-caches.md). Pure, shared
// by the agent app (the agent's What's next, the pipeline's What's next, the
// agent draft column) and anything on the server that needs the same test,
// so no two places keep their own word list.
//
// The links come from the suggestion's modelMeta.nextStepTrackingLinks:
// [{ number, carrier, url, source }], the parent order's first ('parent'),
// then each replacement's. Never put into the step's text.

// A step about checking a package: it mentions tracking, the carrier's site,
// status or scans, INSERTCARRIERSTATUS, lost in transit, or delivered / not
// delivered, or asks to track one. A step about anything else (a refund, a
// note, the reply) is not, and neither is one about a return's package.
export const PACKAGE_CHECK_STEP_PATTERNS = Object.freeze([
  /\btracking\b/i,
  /\btrack (?:the|this|that|its|their|each|both|all|it|them|\d+)\b/i,
  /\bcarrier(?:'s)?\b[^.!?\n]{0,30}?\b(?:site|website|page|status|scans?)\b/i,
  /\b(?:site|website|page|status|scans?)\b[^.!?\n]{0,15}?\bcarrier\b/i,
  /\bscan(?:s|ned)?\b/i,
  /INSERTCARRIERSTATUS/,
  /\blost in transit\b/i,
  /\b(?:not |un|never )?delivered\b/i,
])

// The order's tracking numbers are its OUTBOUND shipments (to the customer).
// A step about a return (a return label, an RMA, a package coming back) is
// about a different package, so it never gets them on a link line.
export const RETURN_PACKAGE_STEP_RE = /\b(?:returns?|returned|returning|RMA|RTN|label)\b/i

export function isPackageCheckStep(text) {
  const s = String(text ?? '')
  if (!s.trim()) return false
  if (RETURN_PACKAGE_STEP_RE.test(s)) return false
  return PACKAGE_CHECK_STEP_PATTERNS.some((re) => re.test(s))
}

// The links, cleaned: entries with a number, one per number, the parent
// order's first (stable otherwise).
export function orderedTrackingLinks(links) {
  const seen = new Set()
  const list = []
  for (const l of Array.isArray(links) ? links : []) {
    const number = String(l?.number ?? '').trim()
    if (!number || seen.has(number.toUpperCase())) continue
    seen.add(number.toUpperCase())
    list.push({ number, carrier: l?.carrier ? String(l.carrier) : null, url: l?.url ? String(l.url) : null, source: l?.source ?? null })
  }
  return [...list.filter((l) => l.source === 'parent'), ...list.filter((l) => l.source !== 'parent')]
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// How one What's next line shows with the order's tracking:
//   segments  the text in pieces, { text } or { text, link } where a number
//             of the order appears in it (that number becomes a link)
//   linkLine  the links to show on a second line ("Tracking: <number>
//             (<carrier>)"), when the line asks to check a package but names
//             no number of the order; else null
// With no tracking on the order, or a line that is not about a package and
// names no number, the line is one plain segment and no link line.
export function nextStepTrackingView(text, links) {
  const s = String(text ?? '')
  const list = orderedTrackingLinks(links)
  if (!list.length || !s) return { segments: [{ text: s }], linkLine: null }
  const byNumber = new Map(list.map((l) => [l.number.toUpperCase(), l]))
  const re = new RegExp(`(?<![A-Za-z0-9])(${list.map((l) => escapeRe(l.number)).sort((a, b) => b.length - a.length).join('|')})(?![A-Za-z0-9])`, 'gi')
  const segments = []
  let last = 0
  let named = false
  for (const m of s.matchAll(re)) {
    if (m.index > last) segments.push({ text: s.slice(last, m.index) })
    segments.push({ text: m[0], link: byNumber.get(m[0].toUpperCase()) })
    last = m.index + m[0].length
    named = true
  }
  if (last < s.length) segments.push({ text: s.slice(last) })
  if (named) return { segments, linkLine: null }
  return { segments: [{ text: s }], linkLine: isPackageCheckStep(s) ? list : null }
}

// "<number> (<carrier>)", the link line's text for one entry.
export function nextStepTrackingLabel(link) {
  return `${link?.number ?? ''}${link?.carrier ? ` (${link.carrier})` : ''}`
}
